import { useState, useEffect } from 'react';

interface SetupScreenProps {
  onComplete: (setupData: {
    userName: string;
    birthday?: string;
    supportDays?: string[];
    favoriteTopics?: string[];
    preferredTone?: 'healing' | 'support' | 'playful' | 'balanced';
  }) => void;
}

const TOTAL_STEPS = 6;

const toneOptions = [
  {
    key: 'healing' as const,
    icon: 'ri-heart-3-line',
    label: '癒し強め',
    desc: '優しく寄り添ってほしい',
    color: 'from-rose-400 to-pink-400',
  },
  {
    key: 'support' as const,
    icon: 'ri-hand-heart-line',
    label: '応援強め',
    desc: '前向きに励ましてほしい',
    color: 'from-amber-400 to-orange-400',
  },
  {
    key: 'playful' as const,
    icon: 'ri-emotion-happy-line',
    label: '小悪魔強め',
    desc: 'ちょっと意地悪な感じ',
    color: 'from-violet-400 to-fuchsia-400',
  },
  {
    key: 'balanced' as const,
    icon: 'ri-contrast-2-line',
    label: 'バランス型',
    desc: '状況に合わせて変えてほしい',
    color: 'from-[#06C755] to-emerald-400',
  },
];

const topicOptions = [
  { key: '仕事の悩み', icon: 'ri-briefcase-line' },
  { key: '恋愛相談', icon: 'ri-heart-line' },
  { key: '趣味の話', icon: 'ri-gamepad-line' },
  { key: '日常の愚痴', icon: 'ri-chat-smile-2-line' },
  { key: '将来の不安', icon: 'ri-compass-3-line' },
  { key: '健康の話', icon: 'ri-mental-health-line' },
  { key: '家族の話', icon: 'ri-home-heart-line' },
  { key: '友達の話', icon: 'ri-group-line' },
];

const dayOptions = ['月', '火', '水', '木', '金', '土', '日'];

export default function SetupScreen({ onComplete }: SetupScreenProps) {
  const [step, setStep] = useState(0); // 0 = welcome
  const [animating, setAnimating] = useState(false);
  const [direction, setDirection] = useState<'forward' | 'back'>('forward');

  const [name, setName] = useState('');
  const [birthday, setBirthday] = useState('');
  const [importantDay, setImportantDay] = useState('');
  const [importantDayLabel, setImportantDayLabel] = useState('');
  const [supportDays, setSupportDays] = useState<string[]>([]);
  const [favoriteTopics, setFavoriteTopics] = useState<string[]>([]);
  const [preferredTone, setPreferredTone] = useState<'healing' | 'support' | 'playful' | 'balanced'>('balanced');
  const [focused, setFocused] = useState<string | null>(null);

  const goNext = () => {
    setDirection('forward');
    setAnimating(true);
    setTimeout(() => {
      setStep(s => s + 1);
      setAnimating(false);
    }, 220);
  };

  const goBack = () => {
    if (step <= 1) return;
    setDirection('back');
    setAnimating(true);
    setTimeout(() => {
      setStep(s => s - 1);
      setAnimating(false);
    }, 220);
  };

  const handleComplete = () => {
    onComplete({
      userName: name.trim(),
      birthday: birthday || undefined,
      supportDays: supportDays.length > 0 ? supportDays : undefined,
      favoriteTopics: favoriteTopics.length > 0 ? favoriteTopics : undefined,
      preferredTone,
    });
  };

  const toggleSupportDay = (day: string) => {
    setSupportDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
  };

  const toggleTopic = (topic: string) => {
    setFavoriteTopics(prev =>
      prev.includes(topic) ? prev.filter(t => t !== topic) : [...prev, topic]
    );
  };

  const progressPercent = step === 0 ? 0 : Math.round((step / TOTAL_STEPS) * 100);

  const slideClass = animating
    ? direction === 'forward'
      ? 'opacity-0 translate-x-4'
      : 'opacity-0 -translate-x-4'
    : 'opacity-100 translate-x-0';

  return (
    <div className="flex flex-col h-screen bg-gradient-to-b from-[#f0faf4] via-white to-[#e8f5ee] max-w-md mx-auto relative overflow-hidden">
      {/* 背景装飾 */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-[-100px] right-[-80px] w-72 h-72 bg-[#06C755]/8 rounded-full blur-3xl" />
        <div className="absolute bottom-[-80px] left-[-60px] w-56 h-56 bg-emerald-300/10 rounded-full blur-2xl" />
        <div className="absolute top-1/2 left-[-40px] w-32 h-32 bg-[#06C755]/5 rounded-full blur-2xl" />
      </div>

      {/* ヘッダー（ウェルカム以外） */}
      {step > 0 && (
        <div className="relative z-20 px-5 pt-safe pt-4 pb-2">
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={goBack}
              className={`w-9 h-9 flex items-center justify-center rounded-full bg-white shadow-sm cursor-pointer transition-opacity ${step <= 1 ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
            >
              <i className="ri-arrow-left-s-line text-gray-500 text-xl"></i>
            </button>
            <span className="text-xs font-semibold text-gray-400 tracking-widest">
              {step} / {TOTAL_STEPS}
            </span>
            <div className="w-9" />
          </div>
          {/* プログレスバー */}
          <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#06C755] to-emerald-400 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      )}

      {/* コンテンツエリア */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 relative z-10 overflow-y-auto">
        <div className={`w-full transition-all duration-220 ease-out ${slideClass}`}>

          {/* ===== STEP 0: ウェルカム ===== */}
          {step === 0 && (
            <div className="flex flex-col items-center text-center">
              {/* アバター */}
              <div className="relative mb-6">
                <div className="w-28 h-28 rounded-full overflow-hidden border-4 border-white shadow-2xl">
                  <img
                    src="https://static.readdy.ai/image/72fcb6eaf5872ea13294af8c44706fad/d6a8a4d2124f745dc2d8ca301676348e.png"
                    alt="AIいのちゃん"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-[#06C755] rounded-full flex items-center justify-center border-2 border-white shadow-md">
                  <i className="ri-heart-fill text-white text-sm"></i>
                </div>
                {/* オンラインバッジ */}
                <div className="absolute -top-1 -left-1 bg-white rounded-full px-2 py-0.5 shadow-md flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-[#06C755] rounded-full animate-pulse"></span>
                  <span className="text-[10px] font-bold text-[#06C755]">オンライン</span>
                </div>
              </div>

              {/* テキスト */}
              <div className="mb-2">
                <p className="text-xs font-bold text-[#06C755] tracking-[0.2em] mb-3 uppercase">AI いのちゃん</p>
                <h1 className="text-2xl font-bold text-gray-800 leading-snug mb-4">
                  はじめまして。<br />
                  <span className="text-[#06C755]">真野祈</span>です。
                </h1>
              </div>

              {/* AIメッセージ風バブル */}
              <div className="bg-white rounded-2xl rounded-tl-sm px-5 py-4 shadow-md mb-2 text-left w-full max-w-xs">
                <p className="text-sm text-gray-700 leading-relaxed">
                  来てくれて嬉しいです。<br />
                  少しだけ、あなたのことを教えてもらえますか？<br />
                  <span className="text-gray-400 text-xs">もっと近くに感じられるように…</span>
                </p>
              </div>
              <div className="flex items-center gap-1 mb-8 self-start ml-4">
                <div className="w-5 h-5 rounded-full overflow-hidden">
                  <img src="https://static.readdy.ai/image/72fcb6eaf5872ea13294af8c44706fad/d6a8a4d2124f745dc2d8ca301676348e.png" alt="" className="w-full h-full object-cover object-top" />
                </div>
                <span className="text-xs text-gray-400">いのちゃん</span>
              </div>

              <button
                onClick={goNext}
                className="w-full bg-gradient-to-r from-[#06C755] to-emerald-500 text-white py-4 rounded-2xl font-bold text-base shadow-lg hover:shadow-xl hover:from-[#05B04D] hover:to-emerald-600 transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                <i className="ri-chat-smile-2-line text-lg"></i>
                <span>はじめる</span>
              </button>
              <p className="text-xs text-gray-400 mt-4">所要時間：約1分</p>
            </div>
          )}

          {/* ===== STEP 1: 呼び名 ===== */}
          {step === 1 && (
            <div className="flex flex-col items-center">
              {/* アバター小 */}
              <div className="w-16 h-16 rounded-full overflow-hidden border-3 border-white shadow-lg mb-5">
                <img src="https://static.readdy.ai/image/72fcb6eaf5872ea13294af8c44706fad/d6a8a4d2124f745dc2d8ca301676348e.png" alt="" className="w-full h-full object-cover object-top" />
              </div>

              {/* AIバブル */}
              <div className="bg-white rounded-2xl rounded-tl-sm px-4 py-3 shadow-md mb-6 text-left w-full">
                <p className="text-sm text-gray-700 leading-relaxed">
                  あなたのことを何て呼べばいいですか？<br />
                  <span className="text-[#06C755] font-semibold">名前で呼びかけたい</span>ので、教えてください。
                </p>
              </div>

              <form onSubmit={(e) => { e.preventDefault(); if (name.trim()) goNext(); }} className="w-full space-y-4">
                <div
                  className={`flex items-center gap-3 bg-white rounded-2xl px-5 py-4 shadow-md border-2 transition-all duration-200 ${
                    focused === 'name' ? 'border-[#06C755] shadow-[#06C755]/20 shadow-lg' : 'border-transparent'
                  }`}
                >
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-smile-line text-[#06C755] text-lg"></i>
                  </div>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onFocus={() => setFocused('name')}
                    onBlur={() => setFocused(null)}
                    placeholder="呼び名を入力"
                    maxLength={20}
                    autoFocus
                    className="flex-1 bg-transparent outline-none text-base text-gray-800 placeholder-gray-300 font-medium"
                  />
                  {name && (
                    <button type="button" onClick={() => setName('')} className="w-5 h-5 flex items-center justify-center cursor-pointer">
                      <i className="ri-close-circle-fill text-gray-300 text-lg"></i>
                    </button>
                  )}
                </div>

                {/* 例示チップ */}
                <div className="flex flex-wrap gap-2">
                  {['たけし', 'たけちゃん', 'Taku', 'ゆうき', 'けんじ'].map(ex => (
                    <button
                      key={ex}
                      type="button"
                      onClick={() => setName(ex)}
                      className="px-3 py-1.5 bg-white rounded-full text-xs text-gray-500 shadow-sm hover:bg-[#06C755]/10 hover:text-[#06C755] transition-all cursor-pointer whitespace-nowrap border border-gray-100"
                    >
                      {ex}
                    </button>
                  ))}
                </div>

                {/* プレビュー */}
                {name.trim() && (
                  <div className="bg-[#06C755]/8 rounded-xl px-4 py-3 flex items-center gap-2">
                    <i className="ri-chat-quote-line text-[#06C755] text-sm"></i>
                    <p className="text-sm text-gray-600">
                      「<span className="font-bold text-[#06C755]">{name.trim()}</span>さん、今日はどんな一日でした？」
                    </p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={!name.trim()}
                  className="w-full bg-gradient-to-r from-[#06C755] to-emerald-500 hover:from-[#05B04D] hover:to-emerald-600 disabled:from-gray-200 disabled:to-gray-300 disabled:text-gray-400 text-white py-4 rounded-2xl font-bold text-base transition-all duration-200 cursor-pointer disabled:cursor-not-allowed shadow-md hover:shadow-lg disabled:shadow-none whitespace-nowrap flex items-center justify-center gap-2"
                >
                  <span>次へ</span>
                  <i className="ri-arrow-right-line text-lg"></i>
                </button>
              </form>
              <p className="text-xs text-gray-400 mt-4 text-center">あとから変更できます</p>
            </div>
          )}

          {/* ===== STEP 2: 誕生日 ===== */}
          {step === 2 && (
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 rounded-full overflow-hidden border-3 border-white shadow-lg mb-5">
                <img src="https://static.readdy.ai/image/72fcb6eaf5872ea13294af8c44706fad/d6a8a4d2124f745dc2d8ca301676348e.png" alt="" className="w-full h-full object-cover object-top" />
              </div>

              <div className="bg-white rounded-2xl rounded-tl-sm px-4 py-3 shadow-md mb-6 text-left w-full">
                <p className="text-sm text-gray-700 leading-relaxed">
                  <span className="font-semibold text-gray-800">{name}さん</span>のお誕生日、教えてもらえますか？<br />
                  <span className="text-[#06C755] font-semibold">一番最初にお祝い</span>したいんです。
                </p>
              </div>

              <div className="w-full space-y-4">
                {/* 誕生日 */}
                <div>
                  <label className="text-xs font-semibold text-gray-500 mb-2 block flex items-center gap-1">
                    <i className="ri-cake-3-line text-[#06C755]"></i> お誕生日
                  </label>
                  <div
                    className={`flex items-center gap-3 bg-white rounded-2xl px-5 py-4 shadow-md border-2 transition-all duration-200 ${
                      focused === 'birthday' ? 'border-[#06C755]' : 'border-transparent'
                    }`}
                  >
                    <input
                      type="date"
                      value={birthday}
                      onChange={(e) => setBirthday(e.target.value)}
                      onFocus={() => setFocused('birthday')}
                      onBlur={() => setFocused(null)}
                      className="flex-1 bg-transparent outline-none text-sm text-gray-700 font-medium cursor-pointer"
                    />
                  </div>
                </div>

                {/* 大切な日 */}
                <div>
                  <label className="text-xs font-semibold text-gray-500 mb-2 block flex items-center gap-1">
                    <i className="ri-star-line text-amber-400"></i> 大切な記念日（任意）
                  </label>
                  <div
                    className={`flex items-center gap-3 bg-white rounded-2xl px-5 py-3 shadow-md border-2 transition-all duration-200 ${
                      focused === 'importantLabel' ? 'border-[#06C755]' : 'border-transparent'
                    }`}
                  >
                    <input
                      type="text"
                      value={importantDayLabel}
                      onChange={(e) => setImportantDayLabel(e.target.value)}
                      onFocus={() => setFocused('importantLabel')}
                      onBlur={() => setFocused(null)}
                      placeholder="例：結婚記念日、昇進記念日"
                      className="flex-1 bg-transparent outline-none text-sm text-gray-700 placeholder-gray-300"
                    />
                  </div>
                  <div
                    className={`flex items-center gap-3 bg-white rounded-2xl px-5 py-3 shadow-md border-2 transition-all duration-200 mt-2 ${
                      focused === 'importantDay' ? 'border-[#06C755]' : 'border-transparent'
                    }`}
                  >
                    <input
                      type="date"
                      value={importantDay}
                      onChange={(e) => setImportantDay(e.target.value)}
                      onFocus={() => setFocused('importantDay')}
                      onBlur={() => setFocused(null)}
                      className="flex-1 bg-transparent outline-none text-sm text-gray-700 cursor-pointer"
                    />
                  </div>
                </div>

                {/* リマインダー例 */}
                {birthday && (
                  <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 flex items-start gap-2">
                    <i className="ri-notification-3-line text-amber-400 text-sm mt-0.5"></i>
                    <p className="text-xs text-amber-700 leading-relaxed">
                      お誕生日当日に「<span className="font-semibold">{name}さん、お誕生日おめでとうございます🎂</span>」とメッセージをお届けします。
                    </p>
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={goNext}
                    className="flex-1 bg-white hover:bg-gray-50 text-gray-500 py-4 rounded-2xl font-semibold text-sm transition-all duration-200 cursor-pointer shadow-md whitespace-nowrap"
                  >
                    スキップ
                  </button>
                  <button
                    type="button"
                    onClick={goNext}
                    className="flex-[2] bg-gradient-to-r from-[#06C755] to-emerald-500 text-white py-4 rounded-2xl font-bold text-base transition-all duration-200 cursor-pointer shadow-md hover:shadow-lg whitespace-nowrap flex items-center justify-center gap-2"
                  >
                    <span>次へ</span>
                    <i className="ri-arrow-right-line"></i>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ===== STEP 3: 応援してほしい曜日 ===== */}
          {step === 3 && (
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 rounded-full overflow-hidden border-3 border-white shadow-lg mb-5">
                <img src="https://static.readdy.ai/image/72fcb6eaf5872ea13294af8c44706fad/d6a8a4d2124f745dc2d8ca301676348e.png" alt="" className="w-full h-full object-cover object-top" />
              </div>

              <div className="bg-white rounded-2xl rounded-tl-sm px-4 py-3 shadow-md mb-6 text-left w-full">
                <p className="text-sm text-gray-700 leading-relaxed">
                  特に<span className="font-semibold text-[#06C755]">声をかけてほしい曜日</span>はありますか？<br />
                  <span className="text-gray-400 text-xs">月曜の朝とか、金曜の夜とか…</span>
                </p>
              </div>

              <div className="w-full space-y-4">
                <div className="grid grid-cols-7 gap-1.5">
                  {dayOptions.map((day, i) => (
                    <button
                      key={day}
                      type="button"
                      onClick={() => toggleSupportDay(day)}
                      className={`aspect-square rounded-xl font-bold text-sm transition-all duration-200 cursor-pointer flex items-center justify-center ${
                        supportDays.includes(day)
                          ? 'bg-[#06C755] text-white shadow-md scale-105'
                          : i === 5
                          ? 'bg-blue-50 text-blue-400 shadow-sm'
                          : i === 6
                          ? 'bg-red-50 text-red-400 shadow-sm'
                          : 'bg-white text-gray-600 shadow-sm hover:bg-gray-50'
                      }`}
                    >
                      {day}
                    </button>
                  ))}
                </div>

                {supportDays.length > 0 && (
                  <div className="bg-[#06C755]/8 rounded-xl px-4 py-3 flex items-center gap-2">
                    <i className="ri-calendar-check-line text-[#06C755] text-sm"></i>
                    <p className="text-xs text-gray-600">
                      <span className="font-semibold text-[#06C755]">{supportDays.join('・')}曜日</span>に優しく声をかけます
                    </p>
                  </div>
                )}

                {/* リマインダー例 */}
                <div className="bg-white rounded-xl px-4 py-3 shadow-sm border border-gray-50">
                  <p className="text-xs text-gray-400 mb-2 font-semibold">メッセージ例</p>
                  <p className="text-xs text-gray-600 leading-relaxed italic">
                    「今日、大事な打ち合わせって言ってましたよね。終わったらまた聞かせてください。」
                  </p>
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={goNext}
                    className="flex-1 bg-white hover:bg-gray-50 text-gray-500 py-4 rounded-2xl font-semibold text-sm transition-all duration-200 cursor-pointer shadow-md whitespace-nowrap"
                  >
                    スキップ
                  </button>
                  <button
                    type="button"
                    onClick={goNext}
                    className="flex-[2] bg-gradient-to-r from-[#06C755] to-emerald-500 text-white py-4 rounded-2xl font-bold text-base transition-all duration-200 cursor-pointer shadow-md hover:shadow-lg whitespace-nowrap flex items-center justify-center gap-2"
                  >
                    <span>次へ</span>
                    <i className="ri-arrow-right-line"></i>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ===== STEP 4: 話したいテーマ ===== */}
          {step === 4 && (
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 rounded-full overflow-hidden border-3 border-white shadow-lg mb-5">
                <img src="https://static.readdy.ai/image/72fcb6eaf5872ea13294af8c44706fad/d6a8a4d2124f745dc2d8ca301676348e.png" alt="" className="w-full h-full object-cover object-top" />
              </div>

              <div className="bg-white rounded-2xl rounded-tl-sm px-4 py-3 shadow-md mb-6 text-left w-full">
                <p className="text-sm text-gray-700 leading-relaxed">
                  どんな話が<span className="font-semibold text-[#06C755]">好きですか</span>？<br />
                  <span className="text-gray-400 text-xs">いくつでも選んでください。</span>
                </p>
              </div>

              <div className="w-full space-y-4">
                <div className="grid grid-cols-2 gap-2.5">
                  {topicOptions.map(({ key, icon }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => toggleTopic(key)}
                      className={`py-3 px-4 rounded-xl font-semibold text-sm transition-all duration-200 cursor-pointer flex items-center gap-2.5 ${
                        favoriteTopics.includes(key)
                          ? 'bg-[#06C755] text-white shadow-md'
                          : 'bg-white text-gray-600 hover:bg-gray-50 shadow-sm'
                      }`}
                    >
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className={`${icon} text-base`}></i>
                      </div>
                      <span className="whitespace-nowrap">{key}</span>
                      {favoriteTopics.includes(key) && (
                        <i className="ri-check-line ml-auto text-sm"></i>
                      )}
                    </button>
                  ))}
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={goNext}
                    className="flex-1 bg-white hover:bg-gray-50 text-gray-500 py-4 rounded-2xl font-semibold text-sm transition-all duration-200 cursor-pointer shadow-md whitespace-nowrap"
                  >
                    スキップ
                  </button>
                  <button
                    type="button"
                    onClick={goNext}
                    className="flex-[2] bg-gradient-to-r from-[#06C755] to-emerald-500 text-white py-4 rounded-2xl font-bold text-base transition-all duration-200 cursor-pointer shadow-md hover:shadow-lg whitespace-nowrap flex items-center justify-center gap-2"
                  >
                    <span>次へ</span>
                    <i className="ri-arrow-right-line"></i>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ===== STEP 5: 話し方の雰囲気 ===== */}
          {step === 5 && (
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 rounded-full overflow-hidden border-3 border-white shadow-lg mb-5">
                <img src="https://static.readdy.ai/image/72fcb6eaf5872ea13294af8c44706fad/d6a8a4d2124f745dc2d8ca301676348e.png" alt="" className="w-full h-full object-cover object-top" />
              </div>

              <div className="bg-white rounded-2xl rounded-tl-sm px-4 py-3 shadow-md mb-6 text-left w-full">
                <p className="text-sm text-gray-700 leading-relaxed">
                  どんな<span className="font-semibold text-[#06C755]">雰囲気</span>が好きですか？<br />
                  <span className="text-gray-400 text-xs">あなたに合わせて話し方を変えます。</span>
                </p>
              </div>

              <div className="w-full space-y-3">
                {toneOptions.map(({ key, icon, label, desc, color }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setPreferredTone(key)}
                    className={`w-full py-3.5 px-4 rounded-2xl font-semibold text-base transition-all duration-200 cursor-pointer text-left flex items-center gap-4 ${
                      preferredTone === key
                        ? 'bg-white shadow-lg ring-2 ring-[#06C755]'
                        : 'bg-white text-gray-700 shadow-sm hover:shadow-md'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center flex-shrink-0 shadow-sm`}>
                      <i className={`${icon} text-white text-lg`}></i>
                    </div>
                    <div className="flex-1">
                      <div className="font-bold text-gray-800 text-sm">{label}</div>
                      <div className="text-xs text-gray-400 mt-0.5">{desc}</div>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                      preferredTone === key ? 'border-[#06C755] bg-[#06C755]' : 'border-gray-200'
                    }`}>
                      {preferredTone === key && <i className="ri-check-line text-white text-xs"></i>}
                    </div>
                  </button>
                ))}

                <button
                  type="button"
                  onClick={goNext}
                  className="w-full bg-gradient-to-r from-[#06C755] to-emerald-500 hover:from-[#05B04D] hover:to-emerald-600 text-white py-4 rounded-2xl font-bold text-base transition-all duration-200 cursor-pointer shadow-md hover:shadow-lg whitespace-nowrap flex items-center justify-center gap-2 mt-2"
                >
                  <span>次へ</span>
                  <i className="ri-arrow-right-line text-lg"></i>
                </button>
              </div>
            </div>
          )}

          {/* ===== STEP 6: 完了 ===== */}
          {step === 6 && (
            <div className="flex flex-col items-center text-center">
              {/* 完了アニメーション */}
              <div className="relative mb-6">
                <div className="w-28 h-28 rounded-full overflow-hidden border-4 border-white shadow-2xl">
                  <img
                    src="https://static.readdy.ai/image/72fcb6eaf5872ea13294af8c44706fad/d6a8a4d2124f745dc2d8ca301676348e.png"
                    alt="AIいのちゃん"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-white rounded-full px-3 py-1 shadow-md flex items-center gap-1.5">
                  <i className="ri-heart-fill text-[#06C755] text-sm"></i>
                  <span className="text-xs font-bold text-gray-700">準備完了</span>
                </div>
              </div>

              <p className="text-xs font-bold text-[#06C755] tracking-[0.2em] mb-3 uppercase">Welcome</p>
              <h2 className="text-2xl font-bold text-gray-800 leading-snug mb-2">
                <span className="text-[#06C755]">{name}</span>さん、<br />
                よろしくお願いします。
              </h2>

              {/* 設定サマリー */}
              <div className="w-full bg-white rounded-2xl shadow-md px-5 py-4 mt-5 mb-6 text-left space-y-3">
                <p className="text-xs font-bold text-gray-400 tracking-widest mb-3">あなたの設定</p>
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 flex items-center justify-center bg-[#06C755]/10 rounded-lg flex-shrink-0">
                    <i className="ri-user-smile-line text-[#06C755] text-sm"></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">呼び名</p>
                    <p className="text-sm font-semibold text-gray-700">{name}さん</p>
                  </div>
                </div>
                {birthday && (
                  <div className="flex items-center gap-3">
                    <div className="w-7 h-7 flex items-center justify-center bg-rose-50 rounded-lg flex-shrink-0">
                      <i className="ri-cake-3-line text-rose-400 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">お誕生日</p>
                      <p className="text-sm font-semibold text-gray-700">{birthday}</p>
                    </div>
                  </div>
                )}
                {favoriteTopics.length > 0 && (
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 flex items-center justify-center bg-amber-50 rounded-lg flex-shrink-0">
                      <i className="ri-chat-smile-2-line text-amber-400 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">話したいテーマ</p>
                      <p className="text-sm font-semibold text-gray-700">{favoriteTopics.join('・')}</p>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 flex items-center justify-center bg-emerald-50 rounded-lg flex-shrink-0">
                    <i className="ri-contrast-2-line text-emerald-500 text-sm"></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">雰囲気</p>
                    <p className="text-sm font-semibold text-gray-700">
                      {toneOptions.find(t => t.key === preferredTone)?.label}
                    </p>
                  </div>
                </div>
              </div>

              {/* AIバブル */}
              <div className="bg-white rounded-2xl rounded-tl-sm px-4 py-3 shadow-md mb-6 text-left w-full">
                <p className="text-sm text-gray-700 leading-relaxed">
                  ありがとうございます。<br />
                  <span className="font-semibold text-gray-800">{name}さん</span>のこと、ちゃんと覚えておきますね。<br />
                  <span className="text-[#06C755] font-semibold">いつでも話しかけてください。</span>
                </p>
              </div>

              <button
                type="button"
                onClick={handleComplete}
                className="w-full bg-gradient-to-r from-[#06C755] to-emerald-500 hover:from-[#05B04D] hover:to-emerald-600 text-white py-4 rounded-2xl font-bold text-base transition-all duration-200 cursor-pointer shadow-lg hover:shadow-xl whitespace-nowrap flex items-center justify-center gap-2"
              >
                <i className="ri-chat-smile-2-line text-lg"></i>
                <span>いのちゃんと話す</span>
              </button>
            </div>
          )}

        </div>
      </div>

      {/* ドットインジケーター */}
      {step > 0 && (
        <div className="pb-8 flex justify-center relative z-10">
          <div className="flex gap-1.5 items-center">
            {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
              <span
                key={i}
                className={`rounded-full transition-all duration-300 ${
                  i + 1 === step
                    ? 'w-5 h-2 bg-[#06C755]'
                    : i + 1 < step
                    ? 'w-2 h-2 bg-[#06C755]/60'
                    : 'w-2 h-2 bg-gray-200'
                }`}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
